import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Button } from "./Button";

/**
 * Put text on the clipboard, whatever the browser allows.
 *
 * Three copies of this existed — `apps/admin/.../ShareLinkButton.tsx`,
 * `apps/buyer/.../ShareLinkButton.tsx` and the restaurant money report's
 * inline handler — each with its own `execCommand` fallback and its own
 * "copied" timer. The fallback is the part worth having once: `navigator
 * .clipboard` is unavailable on an insecure origin and can reject when the
 * document is not focused, and a share button that silently does nothing is
 * indistinguishable from one that worked.
 *
 * Returns whether the text actually landed, so a caller can toast on success
 * and offer something else on failure rather than claiming a copy that never
 * happened.
 */
export async function copyText(text: string): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Falls through: an insecure origin, or a document that is not focused.
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
}

/**
 * The "copied" flag and the timer that clears it.
 *
 * Separate from `CopyButton` because the two `ShareLinkButton`s want the flag
 * with their own markup around it, and re-implementing a `setTimeout` cleanup
 * is exactly how one of three copies ends up leaking a timer on unmount.
 */
export function useCopyState(resetMs = 1800): [boolean, () => void] {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), resetMs);
    return () => window.clearTimeout(timer);
  }, [copied, resetMs]);
  return [copied, useCallback(() => setCopied(true), [])];
}

/**
 * A value wrapped so it keeps its own direction inside a plain string.
 *
 * U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP DIRECTIONAL ISOLATE. A clipboard
 * line, a toast body and a translated string are all single text nodes with no
 * element to carry `dir`, so a leading minus on a figure that ends in an
 * Arabic word ("-500 جنيه") is a bidi neutral inside a right-to-left run and
 * renders «500- جنيه» — the OPPOSITE SIGN. This is the only fix available
 * there, and it was first needed on a VAT position an accountant pastes into a
 * filing.
 *
 * Apply it ONLY to values whose sign can change: a positive figure has no
 * neutral to misplace, and invisible control characters are not something to
 * scatter through text someone may paste into a spreadsheet cell.
 */
export function isolateForPlainText(value: string): string {
  return `⁦${value}⁩`;
}

export interface CopyButtonProps {
  /** The exact text to copy. Signed values inside it want `isolateForPlainText`. */
  text: string;
  label: string;
  copiedLabel: string;
  /** Announced when the copy fails and the text is offered another way. */
  failedLabel?: string;
  ariaLabel?: string;
  size?: "sm" | "md";
  variant?: "outline" | "ghost";
  icon?: ReactNode;
  onCopied?: () => void;
  testId?: string;
}

/**
 * Copy-one-thing, as a control.
 *
 * The label swaps to `copiedLabel` for ~1.8s, which is the only confirmation a
 * clipboard write can honestly give. On failure it falls back to a `prompt`
 * holding the text — ugly, and better than a button that appears to work: the
 * text an accountant came for is selectable either way.
 *
 * The swap is announced through `aria-live="polite"` on the label, because a
 * button whose text changes silently tells a screen-reader user nothing about
 * whether the thing they pressed did anything.
 */
export function CopyButton({
  text,
  label,
  copiedLabel,
  failedLabel,
  ariaLabel,
  size = "sm",
  variant = "outline",
  icon,
  onCopied,
  testId,
}: CopyButtonProps) {
  const [copied, markCopied] = useCopyState();

  const handle = async () => {
    const ok = await copyText(text);
    if (ok) {
      markCopied();
      onCopied?.();
      return;
    }
    window.prompt(failedLabel ?? label, text);
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={() => void handle()}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {icon}
      <span aria-live="polite">{copied ? copiedLabel : label}</span>
    </Button>
  );
}
